import { ChatBotUIDateSlot } from "@jugl-web/domain-resources/chat-bot/types";
import { PlainButton } from "@jugl-web/ui-components";
import { cx } from "@jugl-web/utils";
import format from "date-fns/format";
import { FC, useMemo } from "react";
import isSameDay from "date-fns/isSameDay";
import { TimezoneSelect } from "./TimezoneSelect";
import { ReactComponent as ArrowLeftIcon } from "./assets/arrow-left.svg";

interface TimeSlotPickerProps {
  slots: ChatBotUIDateSlot[];
  selectedDay: Date;
  clientTimezoneCode: string;
  businessTimezoneCode: string;
  selectedTimezoneType: "business" | "client";
  onSelect: (slotId: string) => void;
  onGoBack: () => void;
  onTimezoneTypeChange: (timezoneType: "business" | "client") => void;
}

export const TimeSlotPicker: FC<TimeSlotPickerProps> = ({
  slots: allSlots,
  selectedDay,
  clientTimezoneCode,
  businessTimezoneCode,
  selectedTimezoneType,
  onSelect,
  onGoBack,
  onTimezoneTypeChange,
}) => {
  const formattedDay = useMemo<string>(
    () => format(selectedDay, "d MMMM, yyyy"),
    [selectedDay]
  );

  const slotsForSelectedDay = useMemo<ChatBotUIDateSlot[]>(
    () =>
      allSlots
        .filter((slot) => isSameDay(slot.startTime, selectedDay))
        .sort((a, b) => a.startTime.getTime() - b.startTime.getTime()),
    [selectedDay, allSlots]
  );

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-3">
        <PlainButton
          onClick={onGoBack}
          className={cx(
            "border-grey-300 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-solid",
            "text-dark cursor-pointer bg-white transition-colors",
            "hover:bg-grey-100 active:bg-grey-200"
          )}
        >
          <ArrowLeftIcon />
        </PlainButton>
        <div className="flex flex-col items-start gap-0.5">
          <span className="text-dark font-secondary text-[15px] font-medium leading-[20px]">
            {formattedDay}
          </span>
          {clientTimezoneCode !== businessTimezoneCode && (
            <TimezoneSelect
              clientTimezoneCode={clientTimezoneCode}
              businessTimezoneCode={businessTimezoneCode}
              selectedTimezoneType={selectedTimezoneType}
              onSelect={onTimezoneTypeChange}
            />
          )}
        </div>
      </div>

      {/* List of available time slots */}
      <div className="jugl__custom-scrollbar -mr-2 mt-3 flex max-h-[224px] flex-col gap-1.5 overflow-y-auto pr-2">
        {slotsForSelectedDay.length === 0 && (
          <span className="text-dark-600 font-secondary text-sm font-normal leading-[150%]">
            No slots available for this day
          </span>
        )}
        {slotsForSelectedDay.map((slot) => (
          <PlainButton
            key={slot.id}
            onClick={() => onSelect(slot.id)}
            className={cx(
              "flex h-10 shrink-0 items-center rounded-lg px-2",
              "font-secondary text-sm leading-[150%] text-[#4F4F4F]",
              "hover:bg-grey-100 active:bg-grey-200 transition-colors"
            )}
          >
            {format(slot.startTime, "h:mm a")}
          </PlainButton>
        ))}
      </div>
    </div>
  );
};
